import React, {useContext, useEffect, useState} from "react";
import {
    Button,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    useTheme,
    Autocomplete,
    TextField
} from "@mui/material";
import {FilterAltRounded, ReplayRounded, SearchRounded} from "@mui/icons-material";
import {useLocation} from "react-router-dom";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useSetRecoilState} from "recoil";
import {yupResolver} from "@hookform/resolvers/yup";
import {IRaSearchForm} from "../../models/models";
import {FormAutocomplete, FormDateTimePicker, FormTextField} from "../../common/form-fields-wrappers";
import {
    RA_SEARCH_FORM_VALIDATION_SCHEMA,
    RA_SEARCH_INITIAL_VALUE,
    RA_STATUS_OPTIONS,
    RA_STATUS_OPTIONS_NO_EXPIRED
} from "../config";
import {raFilterParams} from "../state";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {NetworkScope} from "../../types";
import {useHasScope} from "../../../../config/globalConfig";
import {useMutation} from "@tanstack/react-query";
import {fetchVehicleAcrissAll} from "../services/http.services";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";

const RaFilter = () => {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const location = useLocation();
    const hasScope = useHasScope;

    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [legalEntities] = useState<any[]>(globalData.user?.legalEntities);
    const [dealers, setDealers] = useState<any[]>([]);

    const [filterExpandedState, setFilterExpandedState] = useState(false);
    const [acrissList, setAcrissList] = useState([]);
    const [dmsOptionsList, setDmsOptionsList] = useState(
        globalData.user?.mobilityStores
            .map((elem: any) => {
                return {
                    id : elem.id,
                    code : elem.bpCode,
                    description : elem.name,
                    label : elem.name,
                    type : "LOCATION"
                };
            })
    );

    const setFilterParams = useSetRecoilState(raFilterParams);

    const raStatusOptions = RA_STATUS_OPTIONS;
    const raStatusOptionsNoExpired = RA_STATUS_OPTIONS_NO_EXPIRED;

    const searchRaForm = useForm<IRaSearchForm>({
        defaultValues: RA_SEARCH_INITIAL_VALUE,
        resolver: yupResolver(
            RA_SEARCH_FORM_VALIDATION_SCHEMA
        ),
    });

    const {errors} = searchRaForm.formState;

    /** Search Actions*/
    const handleResetForm = (e: React.SyntheticEvent): void => {
        e.stopPropagation();

        searchRaForm.reset();
        setFilterParams({});
        setFilterExpandedState(false);
    }

    const handleHideViewFilter = (e: React.SyntheticEvent): void => {
        e.stopPropagation();

        setFilterExpandedState(!filterExpandedState);
    }

    const handleSearch = (formData: IRaSearchForm): void => {
        const filters: any = {};
        for (const [key, value] of Object.entries(formData)) {
            if (key === 'keyless') {
                filters[key] = value === '' ? null : Boolean(value);
            } else if (!!value) {
                if (key === 'acrissId') {
                    filters[key] = value.code;
                } else {
                    filters[key] = value;
                }
            }
        }

        setFilterParams(filters);
        setFilterExpandedState(false);
    }

    /** Fetch Acriss List*/
    const fetchVehicleAcrissAllMutation = useMutation(
        fetchVehicleAcrissAll,
        {
            onSuccess: (response: any) => {
                setAcrissList(response.map((elem: any) => {return {code: elem.code, description: elem.code}}));
            },
            onError: (error) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }
        });

    useEffect(() => {
        const tenants: any =
            globalData.user.legalEntities
                .filter((elem: any) => elem.name !== 'all')
                .map((elem: any) => elem.name);
        fetchVehicleAcrissAllMutation.mutate(tenants);

        if (globalData.user.legalEntities.length === 1) {
            searchRaForm.setValue('tenants', globalData.user.legalEntities);
        } else if (globalData.user.legalEntities.length > 1) {
            searchRaForm.setValue('tenants', globalData.user.legalEntities.filter((elem: any) => elem.name !== 'all'));
            searchRaForm.setValue('dealers', globalData.user.dealers.filter((elem: any) => elem.name !== 'all'));
        }

        if (!!location.search) {
            new URLSearchParams(location.search)
                .forEach(( value, key: any) => {
                    if (key === 'customerId') {
                        searchRaForm.setValue('cbId', value);
                    } else {
                        searchRaForm.setValue(key, value);
                    }
                });

            handleSearch(searchRaForm.getValues());
        }
    }, [])

    return (
        <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
            <FormProvider {...searchRaForm}>
                <form onSubmit={searchRaForm.handleSubmit(handleSearch)}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaSearchForm>
                                name='plate'
                                control={searchRaForm.control}
                                label={t("raSearch.table.headerName.plate")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaSearchForm>
                                name='mainDriver'
                                control={searchRaForm.control}
                                label={t("raSearch.table.headerName.mainDriver")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            {
                                hasScope(NetworkScope.RaInsert) &&
                                <FormAutocomplete<IRaSearchForm>
                                    name="raStatus"
                                    control={searchRaForm.control}
                                    label={t("raSearch.table.headerName.raStatus")}
                                    fullWidth
                                    options={raStatusOptions}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                    getOptionLabel={(opt: any) => opt?.description}
                                />
                            }

                            {
                                !hasScope(NetworkScope.RaInsert) &&
                                <FormAutocomplete<IRaSearchForm>
                                    name="raStatus"
                                    control={searchRaForm.control}
                                    label={t("raSearch.table.headerName.raStatus")}
                                    fullWidth
                                    options={raStatusOptionsNoExpired}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                    getOptionLabel={(opt: any) => opt?.description}
                                />
                            }
                        </Grid>

                        {/* Actions */}
                        <Grid item xs={6} md={3} sx={{display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
                            <Button variant={'text'}
                                    color={"accent"}
                                    startIcon={<ReplayRounded />}
                                    onClick={handleResetForm}
                                    sx={{mx: theme.spacing(0.5)}}>
                                {t('common.button.reset')}
                            </Button>

                            <Button variant={'text'}
                                    color={"accent"}
                                    startIcon={<FilterAltRounded />}
                                    onClick={handleHideViewFilter}
                                    sx={{mx: theme.spacing(0.5)}}>
                                {
                                    filterExpandedState ? t('common.button.close') : t('common.advancedFilters')
                                }
                            </Button>

                            <Button color={"accent"}
                                    size={"icon"}
                                    type={"submit"}
                                    sx={{mx: theme.spacing(0.5)}}>
                                <SearchRounded />
                            </Button>
                        </Grid>

                        {
                            (filterExpandedState || !!errors.tenants) &&
                                <Grid container item xs={12} sx={{mt: theme.spacing(2)}} columnSpacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <FormTextField<IRaSearchForm>
                                            name='idRentalAgreement'
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.idRentalAgreement")}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormAutocomplete<IRaSearchForm>
                                            name="pickUpLocation"
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.pickUpLocation")}
                                            fullWidth
                                            options={dmsOptionsList}
                                            isOptionEqualToValue={(option: any, value: any) => option?.bpCode === value?.bpCode}
                                            getOptionLabel={(opt: any) => opt?.description}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormDateTimePicker<IRaSearchForm>
                                            name="pickUpDateFrom"
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.pickUpDateFrom")}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormDateTimePicker<IRaSearchForm>
                                            name="pickUpDateTo"
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.pickUpDateTo")}
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={3}>
                                        <FormTextField<IRaSearchForm>
                                            name='renewalsAndSubstitutions'
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.renewalsAndSubstitutions")}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormAutocomplete<IRaSearchForm>
                                            name="dropOffLocation"
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.dropOffLocation")}
                                            fullWidth
                                            options={dmsOptionsList}
                                            isOptionEqualToValue={(option: any, value: any) => option?.bpCode === value?.bpCode}
                                            getOptionLabel={(opt: any) => opt?.description}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormDateTimePicker<IRaSearchForm>
                                            name="dropOffDateFrom"
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.dropOffDateFrom")}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormDateTimePicker<IRaSearchForm>
                                            name="dropOffDateTo"
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.dropOffDateTo")}
                                        />
                                    </Grid>


                                    {legalEntities.length > 1 &&
                                        <Grid item md={3} xs={12}>
                                            <Controller
                                                control={searchRaForm.control}
                                                name="tenants"
                                                render={({field: {onChange, value}}: any) => (
                                                    <Autocomplete
                                                        limitTags={1}
                                                        disableCloseOnSelect={true}
                                                        multiple
                                                        id={'tenants'}
                                                        options={legalEntities}
                                                        value={value || []}
                                                        getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.name))}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        renderInput={(params) => (
                                                            <TextField {...params}
                                                                       label={t("raSearch.table.headerName.legalEntities")}
                                                                       error={Boolean(errors.tenants)}
                                                                       helperText={t(errors.tenants?.message as string)}
                                                            />
                                                        )}
                                                        onChange={(event, value) => {
                                                            let tmpDealers = globalData.user?.dealers?.filter((dealer: any) => {
                                                                if (value?.length > 0) {
                                                                    return value?.find((legalEntity: any) => dealer.legalEntities.map((dle: any) => dle.id).includes(legalEntity.id));
                                                                } else {
                                                                    return dealer;
                                                                }
                                                            });
                                                            setDealers(tmpDealers);
                                                            searchRaForm.setValue('dealers', []);
                                                            onChange( value);
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    }
                                    {globalData.user?.dealers.length > 1 &&
                                        <Grid item md={3} xs={12}>
                                            <Controller
                                                control={searchRaForm.control}
                                                name="dealers"
                                                render={({field: {onChange, value}}: any) => (
                                                    <Autocomplete
                                                        limitTags={1}
                                                        disableCloseOnSelect={true}
                                                        multiple
                                                        id={'dealers'}
                                                        options={dealers}
                                                        value={value || []}
                                                        getOptionLabel={(option: any) => t('translation:dealers.'.concat(option.name))}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        renderInput={(params) => (
                                                            <TextField {...params}
                                                                       label={t("raSearch.table.headerName.dealers")}
                                                                       error={Boolean(errors.dealers)}
                                                                       helperText={t(errors.dealers?.message as string)}
                                                            />
                                                        )}
                                                        onChange={(event, value) => {
                                                            onChange( value);
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    }

                                    <Grid item xs={6} md={3}>
                                        <FormTextField<IRaSearchForm>
                                            name='cbId'
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.customerId")}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormAutocomplete<IRaSearchForm>
                                            name="acrissId"
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.effectiveCarGroup")}
                                            fullWidth
                                            options={acrissList}
                                            isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                            getOptionLabel={(opt: any) => opt?.description}
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormTextField<IRaSearchForm>
                                            name='reservationNumber'
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.reservationNumber")}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FormTextField<IRaSearchForm>
                                            name='dossier'
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.dossier")}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={3}>
                                        <FormTextField<IRaSearchForm>
                                            name='rfidCard'
                                            control={searchRaForm.control}
                                            label={t("raSearch.table.headerName.rfidCard")}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: 'flex-end'}}>
                                        <Controller
                                            control={searchRaForm.control}
                                            name={"keyless"}
                                            render={({field}) => (
                                                <FormControl variant="standard" sx={{ width: '100%' }}>
                                                    <InputLabel id="keyless-label">{t("raSearch.table.headerName.keyless")}</InputLabel>
                                                    <Select
                                                        labelId="keyless-label"
                                                        id="keyless-label"
                                                        value={field.value}
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                        }}
                                                    >
                                                        <MenuItem value={''}></MenuItem>
                                                        <MenuItem value={0}>{t("raSearch.table.headerName.no")}</MenuItem>
                                                        <MenuItem value={1}>{t("raSearch.table.headerName.yes")}</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: 'flex-end'}}>
                                        <Controller
                                            control={searchRaForm.control}
                                            name={"toBeReconciled"}
                                            render={({field}) => (
                                                <FormControlLabel sx={{my: "auto"}}
                                                                  control={
                                                                      <Switch checked={field.value} sx={{marginLeft: theme.spacing(1)}}
                                                                              onChange={(e) => {
                                                                                  field.onChange(e);
                                                                              }}
                                                                              name="toBeReconciled"/>
                                                                  }
                                                                  label={t("raSearch.table.headerName.toBeReconciled")}
                                                                  labelPlacement="start"
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: 'flex-end'}}>

                                        <Controller
                                            control={searchRaForm.control}
                                            name={"recoveryMode"}
                                            render={({field}) => (
                                                <FormControlLabel sx={{my: "auto"}}
                                                                  control={
                                                                      <Switch checked={field.value} sx={{marginLeft: theme.spacing(1)}}
                                                                              onChange={(e) => {
                                                                                  field.onChange(e);
                                                                              }}
                                                                              name="recoveryMode"/>
                                                                  }
                                                                  label={t("raSearch.table.headerName.recoveryMode")}
                                                                  labelPlacement="start"
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                        }
                    </Grid>
                </form>
            </FormProvider>
        </Paper>
    );
}

export default RaFilter;